import {
  createContext,
  useContext,
  useReducer,
  useEffect,
  useCallback,
  ReactNode,
} from 'react';
import type { WaitlistEntry, WaitlistStatus } from '../types';
import { useAuth } from './AuthContext';

// Storage key prefix (user-specific)
const WAITLIST_STORAGE_PREFIX = 'brickmod-waitlist-';

// Average rental period in days for wait time calculation
const DAYS_PER_RENTAL = 14;

// State interface
interface WaitlistState {
  entries: WaitlistEntry[];
}

// Action types
type WaitlistAction =
  | { type: 'JOIN_WAITLIST'; payload: { setId: string; position: number } }
  | { type: 'LEAVE_WAITLIST'; payload: string }
  | { type: 'PAUSE_HOLD'; payload: { setId: string; pauseUntil: string } }
  | { type: 'RESUME_HOLD'; payload: string }
  | { type: 'UPDATE_POSITION'; payload: { setId: string; position: number } }
  | { type: 'HYDRATE'; payload: WaitlistEntry[] };

// Initial state
const initialState: WaitlistState = {
  entries: [],
};

// Helper function to calculate estimated wait time
function calculateEstimatedWait(position: number): string {
  const days = position * DAYS_PER_RENTAL;
  if (days <= 7) {
    return '1 week';
  } else if (days <= 14) {
    return '2 weeks';
  } else if (days <= 21) {
    return '3 weeks';
  } else if (days <= 28) {
    return '1 month';
  } else if (days <= 42) {
    return '6 weeks';
  } else if (days <= 56) {
    return '2 months';
  } else {
    const months = Math.ceil(days / 30);
    return `${months} months`;
  }
}

// Reducer
function waitlistReducer(state: WaitlistState, action: WaitlistAction): WaitlistState {
  switch (action.type) {
    case 'JOIN_WAITLIST': {
      // Check if already on waitlist
      const exists = state.entries.some((entry) => entry.setId === action.payload.setId);
      if (exists) return state;

      const newEntry: WaitlistEntry = {
        setId: action.payload.setId,
        position: action.payload.position,
        joinedAt: new Date().toISOString(),
        estimatedWait: calculateEstimatedWait(action.payload.position),
        status: 'active' as WaitlistStatus,
      };
      return {
        ...state,
        entries: [...state.entries, newEntry],
      };
    }

    case 'LEAVE_WAITLIST':
      return {
        ...state,
        entries: state.entries.filter((entry) => entry.setId !== action.payload),
      };

    case 'PAUSE_HOLD':
      return {
        ...state,
        entries: state.entries.map((entry) =>
          entry.setId === action.payload.setId
            ? {
                ...entry,
                status: 'paused' as WaitlistStatus,
                pauseUntil: action.payload.pauseUntil,
              }
            : entry
        ),
      };

    case 'RESUME_HOLD':
      return {
        ...state,
        entries: state.entries.map((entry) =>
          entry.setId === action.payload
            ? {
                ...entry,
                status: 'active' as WaitlistStatus,
                pauseUntil: undefined,
              }
            : entry
        ),
      };

    case 'UPDATE_POSITION':
      return {
        ...state,
        entries: state.entries.map((entry) =>
          entry.setId === action.payload.setId
            ? {
                ...entry,
                position: action.payload.position,
                estimatedWait: calculateEstimatedWait(action.payload.position),
              }
            : entry
        ),
      };

    case 'HYDRATE':
      return {
        ...state,
        entries: action.payload,
      };

    default:
      return state;
  }
}

// Context value interface
interface WaitlistContextValue extends WaitlistState {
  joinWaitlist: (setId: string, position?: number) => void;
  leaveWaitlist: (setId: string) => void;
  pauseHold: (setId: string, pauseUntil: string) => void;
  resumeHold: (setId: string) => void;
  getPosition: (setId: string) => number | null;
}

// Create context
const WaitlistContext = createContext<WaitlistContextValue | undefined>(undefined);

// Storage helper functions
function getWaitlistStorageKey(userId: string): string {
  return `${WAITLIST_STORAGE_PREFIX}${userId}`;
}

function saveWaitlistToStorage(userId: string, entries: WaitlistEntry[]): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(getWaitlistStorageKey(userId), JSON.stringify(entries));
  } catch (error) {
    console.warn('Failed to save waitlist to localStorage:', error);
  }
}

function loadWaitlistFromStorage(userId: string): WaitlistEntry[] {
  if (typeof window === 'undefined') return [];
  try {
    const stored = localStorage.getItem(getWaitlistStorageKey(userId));
    if (stored) {
      return JSON.parse(stored) as WaitlistEntry[];
    }
  } catch (error) {
    console.warn('Failed to load waitlist from localStorage:', error);
  }
  return [];
}

// Provider component
interface WaitlistProviderProps {
  children: ReactNode;
}

export function WaitlistProvider({ children }: WaitlistProviderProps) {
  const { user, isAuthenticated } = useAuth();
  const [state, dispatch] = useReducer(waitlistReducer, initialState);

  // Get user ID for storage operations
  const userId = user?.id;

  // Hydrate waitlist from localStorage when user changes
  useEffect(() => {
    if (isAuthenticated && userId) {
      const storedEntries = loadWaitlistFromStorage(userId);
      dispatch({ type: 'HYDRATE', payload: storedEntries });
    } else {
      // Clear waitlist when user logs out
      dispatch({ type: 'HYDRATE', payload: [] });
    }
  }, [isAuthenticated, userId]);

  // Persist waitlist to localStorage when entries change
  useEffect(() => {
    if (userId && isAuthenticated) {
      saveWaitlistToStorage(userId, state.entries);
    }
  }, [state.entries, userId, isAuthenticated]);

  const joinWaitlist = useCallback((setId: string, position: number = 1) => {
    dispatch({ type: 'JOIN_WAITLIST', payload: { setId, position } });
  }, []);

  const leaveWaitlist = useCallback((setId: string) => {
    dispatch({ type: 'LEAVE_WAITLIST', payload: setId });
  }, []);

  const pauseHold = useCallback((setId: string, pauseUntil: string) => {
    dispatch({ type: 'PAUSE_HOLD', payload: { setId, pauseUntil } });
  }, []);

  const resumeHold = useCallback((setId: string) => {
    dispatch({ type: 'RESUME_HOLD', payload: setId });
  }, []);

  const getPosition = useCallback(
    (setId: string): number | null => {
      const entry = state.entries.find((e) => e.setId === setId);
      return entry ? entry.position : null;
    },
    [state.entries]
  );

  const value: WaitlistContextValue = {
    ...state,
    joinWaitlist,
    leaveWaitlist,
    pauseHold,
    resumeHold,
    getPosition,
  };

  return <WaitlistContext.Provider value={value}>{children}</WaitlistContext.Provider>;
}

// Custom hook
// eslint-disable-next-line react-refresh/only-export-components
export function useWaitlist(): WaitlistContextValue {
  const context = useContext(WaitlistContext);
  if (context === undefined) {
    throw new Error('useWaitlist must be used within a WaitlistProvider');
  }
  return context;
}

export default WaitlistContext;
